import {
    IApiAdvertisement,
    IApiBackground,
    IApiBidirectionalTranslation,
    IApiCharacterAvatar,
    IApiFestivalSetting,
    IApiGreetingsWithToggle,
    IApiHints,
    IApiInteraction,
    IApiLayoutMode,
    IApiPhotoSettings,
    IApiQRCode,
    IApiSubtitleSpeed,
    IApiVolume,
    IApiWeather,
    IKioskSettingNative,
    IScheduleMarqueeNew,
} from "../../types";
import {
    DEFAULT_AD_SETTINGS,
    DEFAULT_BACKGROUND_SETTINGS,
    DEFAULT_BIDIRECTIONAL_TRANSLATION_SETTINGS,
    DEFAULT_FESTIVAL_SETTINGS,
    DEFAULT_GREETINGS,
    DEFAULT_HINTS,
    DEFAULT_INTERACTION_SETTINGS,
    DEFAULT_LANGUAGE_SETTINGS,
    DEFAULT_LAYOUT_SETTINGS,
    DEFAULT_MARQUEE_SETTINGS,
    DEFAULT_PHOTO_SETTINGS,
    DEFAULT_QRCODE_SETTINGS,
    DEFAULT_SUBTITLE_SPEED,
    DEFAULT_TIME_ALERTS,
    DEFAULT_VOLUME_SETTINGS,
    DEFAULT_WEATHER_SETTINGS,
} from "../configs/default-values";
import { INITIAL_CHARACTER_AVATAR, INITIAL_GAMES_TOGGLE } from "../configs/initial-values";
import { IApiGamesToggle } from "../types/game-interfaces";

/**
 * 合併 Kiosk 設定
 * 將更新的欄位與現有設定合併，未指定的欄位使用現有值或預設值
 *
 * @param current - 當前的 native 設定
 * @param updates - 要更新的欄位
 * @returns 合併後的完整 native 設定
 */
export const mergeKioskSettings = (
    current: IKioskSettingNative | undefined,
    updates: Partial<IKioskSettingNative>
): IKioskSettingNative => ({
    general: updates.general ?? current?.general ?? DEFAULT_LANGUAGE_SETTINGS,
    schedule_marquee: updates.schedule_marquee ?? current?.schedule_marquee ?? DEFAULT_MARQUEE_SETTINGS,
    ad: updates.ad ?? current?.ad ?? DEFAULT_AD_SETTINGS,
    greetings: updates.greetings ?? current?.greetings ?? DEFAULT_GREETINGS,
    hints: (updates.hints ?? current?.hints ?? DEFAULT_HINTS) as IApiHints,
    QRcode: updates.QRcode ?? current?.QRcode ?? DEFAULT_QRCODE_SETTINGS,
    festival_setting: updates.festival_setting ?? current?.festival_setting ?? DEFAULT_FESTIVAL_SETTINGS,
    photo: updates.photo ?? current?.photo ?? DEFAULT_PHOTO_SETTINGS,
    background: updates.background ?? current?.background ?? DEFAULT_BACKGROUND_SETTINGS,
    volume: updates.volume ?? current?.volume ?? DEFAULT_VOLUME_SETTINGS,
    weather: updates.weather ?? current?.weather ?? DEFAULT_WEATHER_SETTINGS,
    time_alerts: updates.time_alerts ?? current?.time_alerts ?? DEFAULT_TIME_ALERTS,
    interaction: updates.interaction ?? current?.interaction ?? DEFAULT_INTERACTION_SETTINGS,
    layout_mode: updates.layout_mode ?? current?.layout_mode ?? DEFAULT_LAYOUT_SETTINGS,
    subtitle_speed: updates.subtitle_speed ?? current?.subtitle_speed ?? DEFAULT_SUBTITLE_SPEED,
    bidirectional_translation:
        updates.bidirectional_translation ??
        current?.bidirectional_translation ??
        DEFAULT_BIDIRECTIONAL_TRANSLATION_SETTINGS,
    games: updates.games ?? current?.games ?? INITIAL_GAMES_TOGGLE,
    character_avatar: updates.character_avatar ?? current?.character_avatar ?? INITIAL_CHARACTER_AVATAR,
});

/**
 * 建立單一欄位更新的 Kiosk 設定
 * 便捷函數，用於只更新一個欄位時
 */
export const createKioskUpdate = {
    general: (current: IKioskSettingNative | undefined, general: IKioskSettingNative["general"]): IKioskSettingNative =>
        mergeKioskSettings(current, { general }),

    scheduleMarquee: (
        current: IKioskSettingNative | undefined,
        schedule_marquee: IScheduleMarqueeNew
    ): IKioskSettingNative => mergeKioskSettings(current, { schedule_marquee }),

    ad: (current: IKioskSettingNative | undefined, ad: IApiAdvertisement): IKioskSettingNative =>
        mergeKioskSettings(current, { ad }),

    greetings: (current: IKioskSettingNative | undefined, greetings: IApiGreetingsWithToggle): IKioskSettingNative =>
        mergeKioskSettings(current, { greetings }),

    hints: (current: IKioskSettingNative | undefined, hints: IApiHints): IKioskSettingNative =>
        mergeKioskSettings(current, { hints }),

    qrcode: (current: IKioskSettingNative | undefined, QRcode: IApiQRCode): IKioskSettingNative =>
        mergeKioskSettings(current, { QRcode }),

    festivalSetting: (
        current: IKioskSettingNative | undefined,
        festival_setting: IApiFestivalSetting
    ): IKioskSettingNative => mergeKioskSettings(current, { festival_setting }),

    photo: (current: IKioskSettingNative | undefined, photo: IApiPhotoSettings): IKioskSettingNative =>
        mergeKioskSettings(current, { photo }),

    background: (current: IKioskSettingNative | undefined, background: IApiBackground): IKioskSettingNative =>
        mergeKioskSettings(current, { background }),

    volume: (current: IKioskSettingNative | undefined, volume: IApiVolume): IKioskSettingNative =>
        mergeKioskSettings(current, { volume }),

    weather: (current: IKioskSettingNative | undefined, weather: IApiWeather): IKioskSettingNative =>
        mergeKioskSettings(current, { weather }),

    interaction: (current: IKioskSettingNative | undefined, interaction: IApiInteraction): IKioskSettingNative =>
        mergeKioskSettings(current, { interaction }),

    layoutMode: (current: IKioskSettingNative | undefined, layout_mode: IApiLayoutMode): IKioskSettingNative =>
        mergeKioskSettings(current, { layout_mode }),

    subtitleSpeed: (
        current: IKioskSettingNative | undefined,
        subtitle_speed: IApiSubtitleSpeed[]
    ): IKioskSettingNative => mergeKioskSettings(current, { subtitle_speed }),

    bidirectionalTranslation: (
        current: IKioskSettingNative | undefined,
        bidirectional_translation: IApiBidirectionalTranslation
    ): IKioskSettingNative => mergeKioskSettings(current, { bidirectional_translation }),

    games: (current: IKioskSettingNative | undefined, games: IApiGamesToggle): IKioskSettingNative =>
        mergeKioskSettings(current, { games }),

    characterAvatar: (
        current: IKioskSettingNative | undefined,
        character_avatar: IApiCharacterAvatar
    ): IKioskSettingNative => mergeKioskSettings(current, { character_avatar }),
};
